import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import {
  ApplicationSettingsService,
  SearchView,
} from 'src/app/services/application-settings.service';
import { SearchService } from 'src/app/services/search.service';
import { STATES } from 'src/app/utils/states';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import {
  Countries,
  POITypes,
  searchControlValue,
  States,
} from 'src/app/_models/pcmws/singleSearch/singleSearchTypesResponse';
import { EU_STATES } from 'src/app/utils/eu_states';
import { NumericKeyValueState } from 'src/app/_models/state/optionUIState';

export enum Precision {
  WholeNumber = 0,
  Ones = 1,
  Tenths = 2,
  Hundredths = 3,
  Thousandths = 4,
}

@Component({
  selector: 'app-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeneralComponent implements OnInit, OnDestroy {
  @Input() applicationParentForm: FormGroup;
  @Input() hasPremiumSubscription: boolean;
  @Input() isEuRegion: boolean;
  private subscriptionManager = new SubscriptionManager();
  generalForm: FormGroup;
  stateOptions: States[];
  states: States[];
  countryOptions: Countries[];
  placeTypeOptions: POITypes[];
  SearchTypes: string[];
  excludeTypes: searchControlValue[];
  includeTypes: searchControlValue[];
  roundOffDistanceUnit: NumericKeyValueState[];

  constructor(
    private formBuilder: FormBuilder,
    private appSettingsService: ApplicationSettingsService,
    private singleSearchServive: SearchService
  ) {}

  ngOnInit(): void {
    this.intializeFormValues();
    this.init();
  }

  private init() {
    const searchOptions = this.singleSearchServive.getSearchOptions();
    const poiTypes = searchOptions[0];
    const types = searchOptions[1];
    const countries = searchOptions[2];
    const states = searchOptions[3];

    this.placeTypeOptions = poiTypes.POITypes;
    this.countryOptions = countries?.Countries;
    this.stateOptions = states?.States;
    this.states = states?.States;
    this.SearchTypes = types?.SearchTypes;
    this.excludeTypes = types?.SearchTypes.map((val) => {
      return { value: val, disable: false };
    });
    this.includeTypes = types?.SearchTypes.map((val) => {
      return { value: val, disable: false };
    });
    this.roundOffDistanceUnit = [
      {
        display: 'Whole Number',
        value: Precision.WholeNumber,
      },
      {
        display: 'Ones',
        value: Precision.Ones,
      },
      {
        display: 'Tenths',
        value: Precision.Tenths,
      },
      {
        display: 'Hundredths',
        value: Precision.Hundredths,
      },
      {
        display: 'Thousandths',
        value: Precision.Thousandths,
      },
    ];
    this.updateFormValues();
    this.detectChangeValues();
  }

  private intializeFormValues() {
    this.generalForm = this.formBuilder.group({
      includePlaces: [false],
      useCustomPlaces: [false],
      poiTypes: [''],
      countries: [''],
      states: [{ value: '', disabled: true }],
      useMapCenter: [false],
      radiusFromCurrentLonLat: [{ value: '', disabled: true }],
      noOfMaxResults: [''],
      excludeTypes: [''],
      includeTypes: [''],
      precision: [''],
    });
    this.applicationParentForm.addControl('generalInfo', this.generalForm);
    if (!this.hasPremiumSubscription) {
      this.applicationParentForm.get('generalInfo').disable();
    }
  }
  private updateFormValues() {
    // const search = JSON.parse(sessionStorage.getItem('searchView'));
    // this.generalFormControls.includePlaces.setValue(search?.includePlaces);
    this.appSettingsService.searchView$.subscribe((option: SearchView) => {
      this.generalFormControls.includePlaces.setValue(option?.includePlaces, {
        emitEvent: false,
      });
      this.generalFormControls.useMapCenter.setValue(option?.useMapCenter, {
        emitEvent: false,
      });
      this.generalFormControls.radiusFromCurrentLonLat.setValue(
        option?.radiusFromCurrentLonLat,
        {
          emitEvent: false,
        }
      );
      this.generalFormControls.noOfMaxResults.setValue(option?.noOfMaxResults, {
        emitEvent: false,
      });
      this.generalFormControls.useCustomPlaces.setValue(
        option?.useCustomPlaces,
        {
          emitEvent: false,
        }
      );
      this.generalFormControls.poiTypes.setValue(option?.poiTypes, {
        emitEvent: false,
      });
      this.generalFormControls.excludeTypes.setValue(option?.excludeTypes, {
        emitEvent: false,
      });
      this.generalFormControls.countries.setValue(option?.countries, {
        emitEvent: false,
      });
      this.generalFormControls.states.setValue(option?.states, {
        emitEvent: false,
      });
      this.generalFormControls.includeTypes.setValue(option?.includeTypes, {
        emitEvent: false,
      });
      this.setradiusFromCurrentLonLatValue(option?.useMapCenter);
      this.setCountryStateValue(option?.countries);
      this.updateIncludeSearchTypes(option?.includeTypes);
      this.updateExcludeSearchTypes(option?.excludeTypes);
      this.setIncludePlacesValues(option);
      this.setPrecisionValues(option?.precision);
    });

    if (!this.hasPremiumSubscription) {
      this.generalFormControls.radiusFromCurrentLonLat.disable({
        emitEvent: false,
      });
      this.generalFormControls.includePlaces.disable({ emitEvent: false });
      this.generalFormControls.useCustomPlaces.disable({ emitEvent: false });
      this.generalFormControls.poiTypes.disable({ emitEvent: false });
      this.generalFormControls.countries.disable({ emitEvent: false });
      this.generalFormControls.states.disable({ emitEvent: false });
      this.generalFormControls.useMapCenter.disable({ emitEvent: false });
      this.generalFormControls.noOfMaxResults.disable({ emitEvent: false });
      this.generalFormControls.excludeTypes.disable({ emitEvent: false });
      this.generalFormControls.includeTypes.disable({ emitEvent: false });
    }
  }

  get generalFormControls(): { [key: string]: AbstractControl } {
    return (this.applicationParentForm.get('generalInfo') as FormGroup)
      .controls;
  }

  setradiusFromCurrentLonLatValue(option: boolean) {
    if (option) {
      this.generalFormControls.radiusFromCurrentLonLat.enable({
        emitEvent: false,
      });
    } else {
      this.generalFormControls.radiusFromCurrentLonLat.disable({
        emitEvent: false,
      });
    }
  }
  private setIncludePlacesValues(option: SearchView) {
    if (option?.includePlaces) {
      this.generalFormControls.poiTypes.disable({ emitEvent: false });
      this.generalFormControls.excludeTypes.disable({ emitEvent: false });
    } else {
      this.generalFormControls.poiTypes.enable({ emitEvent: false });
      this.generalFormControls.excludeTypes.enable({ emitEvent: false });
    }
  }

  private setCountryStateValue(countries: string[]) {
    if (countries?.length) {
      if (countries?.length > 1) {
        this.generalFormControls.states.disable({ emitEvent: false });
      } else {
        this.generalFormControls.states.enable({ emitEvent: false });
        this.generalFormControls.countries.setValue(countries, {
          emitEvent: false,
        });
        this.stateOptions = this.getStates(countries);
        if (!this.stateOptions?.length) {
          this.generalFormControls.states.disable({ emitEvent: false });
        }
      }
    } else {
      this.stateOptions = this.states;
      this.generalFormControls.states.disable({ emitEvent: false });
    }
  }

  updateIncludeList() {
    const includeTypes = this.includeTypes;
    this.includeTypes = includeTypes.map((type) => {
      type.disable =
        type.value === 'None'
          ? false
          : this.generalFormControls.excludeTypes.value?.some(
              (val) => val === type.value
            );

      return type;
    });
  }

  updateExcludeList() {
    const excludeTypes = this.excludeTypes;
    this.excludeTypes = excludeTypes.map((type) => {
      type.disable =
        type.value === 'None'
          ? false
          : this.generalFormControls.includeTypes.value?.some(
              (val) => val === type.value
            );

      return type;
    });
  }

  private disableOtherOptionsInExcludeTypes(disable: boolean) {
    this.appSettingsService.searchView$.subscribe((value) => {
      this.generalFormControls.useCustomPlaces.setValue(
        value?.useCustomPlaces,
        { emitEvent: false }
      );
    });

    const excludedTypes = this.excludeTypes;
    this.excludeTypes = excludedTypes.map((type) => {
      if (type.value === 'None') {
        type.disable = false;
      } else if (disable) {
        type.disable = disable;
      }
      return type;
    });
    if (!disable) {
      this.updateExcludeList();
    }
  }

  private disableOtherOptionsInIncludeTypes(disable: boolean) {
    this.appSettingsService.searchView$.subscribe((value) => {
      this.generalFormControls.useCustomPlaces.setValue(
        value?.useCustomPlaces,
        { emitEvent: false }
      );
    });
    const includedTypes = this.includeTypes;
    this.includeTypes = includedTypes.map((type) => {
      if (type.value === 'None') {
        type.disable = false;
      } else if (disable) {
        type.disable = disable;
      }
      return type;
    });
    if (!disable) {
      this.updateIncludeList();
    }
  }

  private setExcludedSearchTypes() {
    const hasNone = this.generalFormControls.excludeTypes.value?.some(
      (val) => val === 'None'
    );
    if (!hasNone) {
      this.updateExcludeList();
    }
  }

  private setIncludedSearchTypes() {
    const hasNone = this.generalFormControls.includeTypes.value?.some(
      (val) => val === 'None'
    );
    if (!hasNone) {
      this.updateIncludeList();
    }
  }

  private updateIncludeSearchTypes(value: string[]) {
    this.setExcludedSearchTypes();
    if (value?.some((val) => val === 'None')) {
      this.disableOtherOptionsInIncludeTypes(true);
      this.generalFormControls.includeTypes.setValue(['None'], {
        emitEvent: false,
      });
      this.setExcludedSearchTypes();
    } else {
      if (value?.length) {
        if (value?.some((val) => val === 'CustomPlace')) {
          this.generalFormControls?.useCustomPlaces.setValue(true, {
            emitEvent: false,
          });
        } else {
          this.appSettingsService.searchView$.subscribe((option) => {
            this.generalFormControls.useCustomPlaces.setValue(
              option?.useCustomPlaces,
              { emitEvent: false }
            );
          });
        }
        this.generalFormControls.includeTypes.setValue(value, {
          emitEvent: false,
        });
      } else {
        this.disableOtherOptionsInIncludeTypes(false);
      }
    }
  }

  private setPrecisionValues(precision?: Precision) {
    if (precision) {
      this.generalFormControls.precision.setValue(precision, {
        emitEvent: false,
      });
    } else {
      this.generalFormControls.precision.setValue(Precision.Hundredths, {
        emitEvent: false,
      });
    }
  }

  private updateExcludeSearchTypes(value: string[]) {
    this.setIncludedSearchTypes();
    if (value?.some((val) => val === 'None')) {
      this.disableOtherOptionsInExcludeTypes(true);
      this.generalFormControls.excludeTypes.setValue(['None'], {
        emitEvent: false,
      });
      this.setIncludedSearchTypes();
      this.generalFormControls?.useCustomPlaces.enable();
      this.appSettingsService.searchView$.subscribe((option) => {
        this.generalFormControls.useCustomPlaces.setValue(
          option?.useCustomPlaces,
          { emitEvent: false }
        );
      });
    } else {
      if (value?.length) {
        if (value?.some((val) => val === 'CustomPlace')) {
          this.generalFormControls?.useCustomPlaces.disable();
          this.generalFormControls?.useCustomPlaces.setValue(false, {
            emitEvent: false,
          });
        } else {
          this.appSettingsService.searchView$.subscribe((option) => {
            this.generalFormControls?.useCustomPlaces.enable();
            this.generalFormControls.useCustomPlaces.setValue(
              option?.useCustomPlaces,
              { emitEvent: false }
            );
          });
        }
        this.generalFormControls.excludeTypes.setValue(value, {
          emitEvent: false,
        });
      } else {
        this.generalFormControls.useCustomPlaces.enable({ emitEvent: false });
        this.disableOtherOptionsInExcludeTypes(false);
      }
    }
  }
  private detectChangeValues() {
    this.subscriptionManager.manage =
      this.appSettingsService.searchView$.subscribe((data) => {
        this.generalFormControls.includePlaces.setValue(data?.includePlaces);
      });

    this.subscriptionManager.manage =
      this.generalFormControls.includeTypes.valueChanges.subscribe((value) => {
        if (value) {
          this.updateIncludeSearchTypes(value);
        }
      });
    this.subscriptionManager.manage =
      this.generalFormControls.excludeTypes.valueChanges.subscribe((value) => {
        if (value) {
          this.updateExcludeSearchTypes(value);
        }
      });
    this.subscriptionManager.manage =
      this.generalFormControls.countries.valueChanges.subscribe((value) => {
        if (value) {
          this.setCountryStateValue(value);
        }
      });
    this.subscriptionManager.manage =
      this.generalFormControls.includePlaces.valueChanges.subscribe((value) => {
        if (value) {
          this.generalFormControls.poiTypes.disable({ emitEvent: false });
          this.generalFormControls.excludeTypes.disable({ emitEvent: false });
        } else {
          this.generalFormControls.poiTypes.enable({ emitEvent: false });
          this.generalFormControls.excludeTypes.enable({ emitEvent: false });
        }
      });
    this.subscriptionManager.manage =
      this.generalFormControls.useMapCenter.valueChanges.subscribe((value) => {
        if (value) {
          this.generalFormControls.radiusFromCurrentLonLat.enable({
            emitEvent: false,
          });
        } else {
          this.generalFormControls.radiusFromCurrentLonLat.disable({
            emitEvent: false,
          });
        }
      });
    this.subscriptionManager.manage = this.generalForm.valueChanges.subscribe(
      (value: SearchView) => {
        this.setCountryStateValue(value?.countries);
        if (value?.states && value.countries) {
          this.generalFormControls.states.setValue(value.states, {
            emitEvent: false,
          });
        } else {
          this.generalFormControls.states.setValue(null, {
            emitEvent: false,
          });
        }
        if (value?.poiTypes) {
          this.generalFormControls.poiTypes.setValue(value.poiTypes, {
            emitEvent: false,
          });
        }
        if (value?.noOfMaxResults) {
          this.generalFormControls.noOfMaxResults.setValue(
            value.noOfMaxResults,
            {
              emitEvent: false,
            }
          );
        }

        if (value?.useCustomPlaces !== null) {
          this.generalFormControls.useCustomPlaces.setValue(
            value.useCustomPlaces,
            {
              emitEvent: false,
            }
          );
        }
        if (value?.useMapCenter !== null) {
          this.generalFormControls.useMapCenter.setValue(value.useMapCenter, {
            emitEvent: false,
          });
        }
        this.setIncludePlacesValues(value);
      }
    );
  }

  getStates(countryNames: string[]) {
    const stateList = this.isEuRegion ? EU_STATES : STATES;
    if (countryNames?.length) {
      if (this.generalFormControls.states.enabled) {
        const states = stateList.filter((filteredStates) => {
          return countryNames?.some(
            (selectedCountry) =>
              selectedCountry === filteredStates.countryAbbrevation
          );
        });
        const filterStates = this.states.filter((state) =>
          states?.some(
            (st) =>
              state.Abbrev &&
              st.abbreviation === state.Abbrev &&
              st.name === state.Name
          )
        );
        return filterStates;
      }
      return [];
    }
    return this.states;
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }
}
